import React, { useEffect, useMemo, useState } from 'react'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import RpqsAction from 'rpqs/actions/RpqsAction'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useActions from 'utils/customHook/useActions'
import { push } from '@lagunovsky/redux-react-router'
import { PATH_RPQS_REFERENCIAL_COLLECTIVITY_NEW } from 'home/constants/RouteConstants'
import { getDataWithHeaders } from 'utils/ExportDataUtil'
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import SimpleInput from 'components/forms/SimpleInput'
import Select from 'components/forms/Select'
import { CardTable } from 'components/datatable/NewTable'
import SectorAction from 'referencial/components/sector/actions/SectorAction'

const COLLECTIVITES_HEADERS = ['code', 'name', 'sector', 'nbUnits', 'nbCities', 'nbRPQS', 'lastRPQS']

const CollectivitiesApp = () => {
    const {
        collectivities,
        sectors,
    } = useSelector(store => ({
        collectivities: store.RpqsReducer.collectivities,
        sectors: store.SectorReducer.sectors,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(RpqsAction.fetchAllCollectivities())
        dispatch(SectorAction.fetchSectors())
    }, [])

    const [filter, setFilter] = useState({})
    const [appliedFilter, setAppliedFilter] = useState({})
    const [collectivitiesWithLinks, setCollectivitiesWithLinks] = useState([])

    const collectivitiesSelect = useMemo(() => collectivities.map((e) => (
        {
            ...e,
            codeWithLabel: `${e.name} [${e.code}]`,
        }
    )), [collectivities])

    useEffect(() => {
        Promise.all(collectivities
            .filter(c => c?.id)
            .map((collectivity) => dispatch(RpqsAction.getCollectiviteById(collectivity.id))),
        ).then(setCollectivitiesWithLinks)
    }, [collectivities])

    const filteredData = useMemo(() => {
        const filteredSearchValue = appliedFilter.searchValue
            ? collectivitiesWithLinks.filter(e => `${e.code} ${e.name}`.toLowerCase().includes(appliedFilter.searchValue.toLowerCase()))
            : collectivitiesWithLinks

        const filteredSector = appliedFilter.sector
            ? filteredSearchValue.filter(e => e.sectorCode === appliedFilter.sector)
            : filteredSearchValue

        const filteredCollectivity = appliedFilter.collectivity
            ? filteredSector.filter(e => e.code === appliedFilter.collectivity)
            : filteredSector

        return filteredCollectivity
    }, [appliedFilter, collectivitiesWithLinks])

    useTitle(() => [{
        title: i18n.rpqs,
        href: 'rpqs',
    }, {
        title: i18n.RPQSReferencials,
        href: 'rpqs/referencials',
    }, {
        title: i18n.collectivities,
        href: 'rpqs/referencials/collectivities',
    }], [])

    useActions(() => {
        return {
            new: () => dispatch(push(PATH_RPQS_REFERENCIAL_COLLECTIVITY_NEW)),
            referencialActions: {
                actions: {
                    export: {
                        data: getDataWithHeaders(filteredData, COLLECTIVITES_HEADERS),
                        titleFile: i18n.collectivitiesList,
                    },
                },
            },
        }
    }, [filteredData])

    const data = useMemo(() => filteredData
        .filter(c => c?.id)
        .map(c => (
            {
                id: c.id,
                code: { value: c.code },
                sector: { value: sectors?.find(e => e.id === c.sectorCode)?.name },
                nbUnits: { value: c.link_managementUnits?.length ?? 0 },
                nbCities: { value: c.link_cities?.length ?? 0 },
                name: { value: c.name },
            }
        )), [filteredData, sectors])

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={4}>
                            <SimpleInput
                                title={i18n.search}
                                value={filter.searchValue}
                                onChange={searchValue => setFilter(prev => ({ ...prev, searchValue }))}
                                onEnterKeyPress={() => setAppliedFilter(filter)}
                                data-cy='search_input'
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select
                                label={i18n.sectors}
                                options={sectors}
                                value={filter.sector}
                                onChange={sector => setFilter(prev => ({ ...prev, sector }))}
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select
                                label={i18n.collectivities}
                                options={collectivitiesSelect}
                                value={filter.collectivity}
                                keyLabel={'codeWithLabel'}
                                onChange={collectivity => setFilter(prev => ({ ...prev, collectivity }))}
                            />
                        </Grid>
                        <Grid container size={12} spacing={1} justifyContent='flex-end'>
                            <Button
                                variant='outlined'
                                onClick={() => {
                                    setFilter({})
                                    setAppliedFilter({})
                                }}
                            >
                                {i18n.reinit}
                            </Button>
                            <Button
                                variant='contained'
                                onClick={() => setAppliedFilter(filter)}
                            >
                                {i18n.search}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <CardTable
                title={i18n.collectivitiesList}
                rows={data}
                headers={COLLECTIVITES_HEADERS}
                onClickRow={me => dispatch(push(`/rpqs/referencials/collectivities/${me.id}/dashboard`))}
            />
        </Stack>
    )
}

export default CollectivitiesApp
